/**
 * Rutas ECHEQ bajo el shell privado v3 (misma cobertura que `PrivateRoutes` para `/echeqs/*`).
 *
 * En legacy, `DefaultLayout` aplica `resizableRoute` y reenvía `isDesktop` / `isMobile` a la página.
 * En v3 no hay `DefaultLayout`, así que aquí envolvemos cada pantalla (salvo `EcheqsMainPage`, que ya
 * exporta `compose(..., resizableRoute)`) para conservar breakpoints y `display-changed`.
 */
import React from "react";
import { Route, Redirect } from "react-router-dom";

import { resizableRoute } from "pages/_components/Resizable";
import * as configUtils from "util/config";

import AnulateEcheqAction from "pages/checks/Echeqs/AnulateEcheqAction";
import ConfirmEcheqAction from "pages/checks/Echeqs/ConfirmEcheqAction";
import ConfirmEndorseEcheqAction from "pages/checks/Echeqs/ConfirmEndorseEcheqAction";
import EndorseEcheqStep1 from "pages/checks/Echeqs/EcheqActions/EndorseEcheqStep1";
import NegotiateEcheq from "pages/checks/Echeqs/EcheqActions/NegotiateEcheq";
import EcheqsMainPage from "pages/checks/Echeqs/EcheqsMainPage";
import EmittedEcheqsDetail from "pages/checks/Echeqs/EmittedEcheqsDetail";
import ReceivedEcheqsDetail from "pages/checks/Echeqs/ReceivedEcheqsDetail";
import RequestReturnEcheqAction from "pages/checks/Echeqs/RequestReturnEcheqAction";
import ConfirmAcceptReturnEcheq from "pages/checks/Echeqs/ConfirmAcceptReturnEcheq";
import ConfirmCesionEcheqAction from "pages/checks/Echeqs/ConfirmCesionEcheqAction";
import ConfirmEmitEcheq from "pages/checks/Echeqs/ConfirmEmitEcheq";
import EmitCesion from "pages/checks/Echeqs/EcheqActions/EmitCesion";
import EmitEcheq from "pages/checks/Echeqs/EcheqActions/EmitEcheq";
import EndorseTransaction from "pages/checks/Echeqs/EcheqTransactions/EndorseTransaction";
import EcheqTransaction from "pages/checks/Echeqs/EcheqTransactions/EcheqTransaction";
import MultipleEcheqsTransaction from "pages/checks/Echeqs/EcheqTransactions/MultipleEcheqsTransaction";
import CesionEcheqTransaction from "pages/checks/Echeqs/EcheqTransactions/CesionEcheqTransaction";
import EcheqAcceptReturnTransaction from "pages/checks/Echeqs/EcheqTransactions/EcheqAcceptReturnTransaction";
import EmitEcheqTransaction from "pages/checks/Echeqs/EcheqTransactions/EmitEcheqTransaction";
import ExternalEmitEcheqTransaction from "pages/checks/Echeqs/EcheqTransactions/ExternalEmitEcheqTransaction";
import EmitMassive from "pages/checks/Echeqs/Massive/EmitMassive";
import EmitMassiveSummary from "pages/checks/Echeqs/Massive/EmitMassiveSummary";
import EmitMassiveTicket from "pages/checks/Echeqs/Massive/EmitMassiveTicket";
import BatchDetail from "pages/checks/Echeqs/Massive/BatchDetail";
import EndorseTicketV3Page from "v3/pages/echeqs/EndorseTicketV3Page";

/** Evita crear una nueva clase en cada render de `buildV3EcheqsShellRoutes`. */
const resizableForV3ByPage = new WeakMap();

/** Páginas que ya aplican `resizableRoute` en su propio export. */
const PAGES_WITH_OWN_RESIZE = [EcheqsMainPage, EndorseTicketV3Page];

/** Habilita el comprobante V3 de endoso/negociación/cesión; con la config apagada se sirve el legacy. */
const ENDORSE_TICKET_V3_CONFIG = "frontend.show.managementMassiveEcheq.pay.functionalities";

/**
 * @param {React.ComponentType} Page
 * @returns {React.ComponentType}
 */
function withResizeForV3Shell(Page) {
    if (PAGES_WITH_OWN_RESIZE.includes(Page)) {
        return Page;
    }
    let Wrapped = resizableForV3ByPage.get(Page);
    if (!Wrapped) {
        Wrapped = resizableRoute(Page);
        resizableForV3ByPage.set(Page, Wrapped);
    }
    return Wrapped;
}

/**
 * @param {object} p
 * @param {string} p.home
 * @param {boolean} p.canOperateEcheqs
 * @param {boolean} p.canMassiveEmitEcheq
 * @returns {React.ReactElement[]}
 */
export default function buildV3EcheqsShellRoutes({ home, canOperateEcheqs, canMassiveEmitEcheq }) {
    const guardOperate = (Component) => {
        const Cmp = withResizeForV3Shell(Component);
        return (rp) => {
            if (!canOperateEcheqs) {
                return <Redirect to={home} />;
            }
            return <Cmp {...rp} embeddedInV3Shell />;
        };
    };

    const guardMassive = (Component) => {
        const Cmp = withResizeForV3Shell(Component);
        return (rp) => {
            if (!canMassiveEmitEcheq) {
                return <Redirect to={home} />;
            }
            return <Cmp {...rp} embeddedInV3Shell />;
        };
    };

    const open = (Component) => {
        const Cmp = withResizeForV3Shell(Component);
        return (rp) => <Cmp {...rp} embeddedInV3Shell />;
    };

    const isEndorseTicketV3Enabled = configUtils.getBoolean(ENDORSE_TICKET_V3_CONFIG, false);
    const endorseTicketPage = isEndorseTicketV3Enabled ? EndorseTicketV3Page : EndorseTransaction;
    const cesionTicketPage = isEndorseTicketV3Enabled ? EndorseTicketV3Page : CesionEcheqTransaction;

    return [
        <Route key="/echeqs" exact path="/echeqs" render={guardOperate(EcheqsMainPage)} />,
        <Route
            key="/echeqs/summary"
            exact
            path="/echeqs/summary/:envId/:envName/:state"
            render={guardOperate(EcheqsMainPage)}
        />,
        <Route
            key="/echeqs/single4"
            exact
            path="/echeqs/single/:envId/:envName/:echeqId/:echeqType"
            render={guardOperate(EcheqsMainPage)}
        />,
        <Route
            key="/echeqs/single5"
            exact
            path="/echeqs/single/:envId/:envName/:echeqId/:echeqType/:currency"
            render={guardOperate(EcheqsMainPage)}
        />,
        <Route
            key="/echeqs/action/confirmation"
            exact
            path="/echeqs/action/confirmation"
            render={guardOperate(ConfirmEcheqAction)}
        />,
        <Route
            key="/echeqs/return/confirmation"
            exact
            path="/echeqs/return/confirmation"
            render={guardOperate(RequestReturnEcheqAction)}
        />,
        <Route
            key="/echeqs/anulate/confirmation"
            exact
            path="/echeqs/anulate/confirmation"
            render={guardOperate(AnulateEcheqAction)}
        />,
        <Route
            key="/echeqs/action/endorse"
            exact
            path="/echeqs/action/endorse"
            render={guardOperate(EndorseEcheqStep1)}
        />,
        <Route key="/echeqs/action/cesion" exact path="/echeqs/action/cesion" render={guardOperate(EmitCesion)} />,
        <Route
            key="/echeqs/action/cesion/confirm"
            exact
            path="/echeqs/action/cesion/confirm"
            render={guardOperate(ConfirmCesionEcheqAction)}
        />,
        <Route
            key="/echeqs/action/negotiate"
            exact
            path="/echeqs/action/negotiate"
            render={guardOperate(NegotiateEcheq)}
        />,
        <Route
            key="/echeqs/action/return/accept"
            exact
            path="/echeqs/action/return/accept"
            render={guardOperate(ConfirmAcceptReturnEcheq)}
        />,
        <Route
            key="/echeqs/action/endorse/confirm"
            exact
            path="/echeqs/action/endorse/confirm"
            render={guardOperate(ConfirmEndorseEcheqAction)}
        />,
        <Route
            key="/echeqs/received/detail"
            exact
            path="/echeqs/received/detail/:echeqId"
            render={guardOperate(ReceivedEcheqsDetail)}
        />,
        <Route
            key="/echeqs/emitted/detail"
            exact
            path="/echeqs/emitted/detail/:echeqId"
            render={guardOperate(EmittedEcheqsDetail)}
        />,
        <Route key="/echeqs/confirm/endorse" exact path="/echeqs/confirm/endorse" render={open(endorseTicketPage)} />,
        <Route key="/echeqs/pending/endorse" exact path="/echeqs/pending/endorse" render={open(endorseTicketPage)} />,
        <Route
            key="/echeqs/cancelled/endorse"
            exact
            path="/echeqs/cancelled/endorse"
            render={open(endorseTicketPage)}
        />,
        <Route key="/echeqs/failed/endorse" exact path="/echeqs/failed/endorse" render={open(endorseTicketPage)} />,
        <Route
            key="/echeqs/transaction/action"
            exact
            path="/echeqs/transaction/action"
            render={open(EcheqTransaction)}
        />,
        <Route
            key="/echeqs/pending/certificate"
            exact
            path="/echeqs/pending/certificate"
            render={open(EcheqTransaction)}
        />,
        <Route
            key="/echeqs/return/accept/transaction"
            exact
            path="/echeqs/return/accept/transaction"
            render={open(EcheqAcceptReturnTransaction)}
        />,
        <Route
            key="/echeqs/multiple/:status"
            exact
            path="/echeqs/multiple/:status"
            render={open(MultipleEcheqsTransaction)}
        />,
        <Route key="/echeqs/emit" exact path="/echeqs/emit" render={guardOperate(EmitEcheq)} />,
        <Route
            key="/echeqs/action/emit/confirm"
            exact
            path="/echeqs/action/emit/confirm"
            render={open(ConfirmEmitEcheq)}
        />,
        <Route key="/echeqs/confirm/emit" exact path="/echeqs/confirm/emit" render={open(EmitEcheqTransaction)} />,
        <Route key="/echeqs/failed/emit" exact path="/echeqs/failed/emit" render={open(EmitEcheqTransaction)} />,
        <Route key="/echeqs/pending/emit" exact path="/echeqs/pending/emit" render={open(EmitEcheqTransaction)} />,
        <Route key="/echeqs/cancelled/emit" exact path="/echeqs/cancelled/emit" render={open(EmitEcheqTransaction)} />,
        <Route key="/echeqs/confirm/cesion" exact path="/echeqs/confirm/cesion" render={open(cesionTicketPage)} />,
        <Route key="/echeqs/failed/cesion" exact path="/echeqs/failed/cesion" render={open(cesionTicketPage)} />,
        <Route key="/echeqs/pending/cesion" exact path="/echeqs/pending/cesion" render={open(cesionTicketPage)} />,
        <Route key="/echeqs/cancelled/cesion" exact path="/echeqs/cancelled/cesion" render={open(cesionTicketPage)} />,
        <Route
            key="/echeqs/confirm/external/emit"
            exact
            path="/echeqs/confirm/external/emit"
            render={open(ExternalEmitEcheqTransaction)}
        />,
        <Route
            key="/echeqs/failed/external/emit"
            exact
            path="/echeqs/failed/external/emit"
            render={open(ExternalEmitEcheqTransaction)}
        />,
        <Route
            key="/echeqs/pending/external/emit"
            exact
            path="/echeqs/pending/external/emit"
            render={open(ExternalEmitEcheqTransaction)}
        />,
        <Route
            key="/echeqs/cancelled/external/emit"
            exact
            path="/echeqs/cancelled/external/emit"
            render={open(ExternalEmitEcheqTransaction)}
        />,
        <Route key="/echeqs/emit/massive" exact path="/echeqs/emit/massive" render={guardMassive(EmitMassive)} />,
        <Route
            key="/echeqs/emit/massive/confirm"
            exact
            path="/echeqs/emit/massive/confirm"
            render={open(EmitMassiveSummary)}
        />,
        <Route
            key="/echeqs/emitMassive/confirmation"
            exact
            path="/echeqs/emitMassive/confirmation"
            render={open(EmitMassiveTicket)}
        />,
        <Route
            key="/echeqs/emitMassive/pending"
            exact
            path="/echeqs/emitMassive/pending"
            render={open(EmitMassiveTicket)}
        />,
        <Route
            key="/echeqs/emitMassive/failed"
            exact
            path="/echeqs/emitMassive/failed"
            render={open(EmitMassiveTicket)}
        />,
        <Route
            key="/echeqs/emitMassive/cancelled"
            exact
            path="/echeqs/emitMassive/cancelled"
            render={open(EmitMassiveTicket)}
        />,
        <Route
            key="/echeqs/emit/massive/detail"
            exact
            path="/echeqs/emit/massive/:idTransaction/detail"
            render={open(BatchDetail)}
        />,
    ];
}
